import React, { useState, useEffect } from 'react';
import BrandingEditor from './BrandingEditor';
import WebsiteVisualizer from './WebsiteVisualizer';
import apiService from './Common/Configurations/APIService';
import config from './Common/Configurations/APIConfig';
import { Spinner } from 'react-bootstrap';
import { ToastContainer } from 'react-toastify';

const WebsiteManager = () => {
    const [step, setStep] = useState(1); // 1: Branding, 2: Style
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const safeJSON = (str, fallback = {}) => {
        if (!str || str === 'string' || str === 'null') return fallback;
        try { return JSON.parse(str); } catch { return fallback; }
    };

    useEffect(() => {
        const fetchCurrentConfig = async () => {
            try {
                // ⭐ Capture manageDomain from URL (passed from Admin project)
                const params = new URLSearchParams(window.location.search);
                const manageDomain = params.get('manageDomain');
                
                const host = window.location.hostname.toLowerCase();
                const activeDomain = manageDomain || (host === 'localhost' ? 'epicdestino.in' : host);

                const res = await apiService.getAll(`${config.GETTEMPLATEBYDOMAIN}/${activeDomain}`);
                const d = res.data || res;

                // Parse everything into objects for the editors
                setData({
                    companyTemplateId: d.companyTemplateId,
                    companyId:         d.companyId,
                    branchId:          d.branchId,
                    userId:            d.userId,
                    templateId:        d.templateId,
                    templateType:      d.templateType,
                    templateVersionId: d.templateVersionId,
                    templateReference: d.templateReference || d.templateRefference, // Fix potential spelling mismatch
                    domain:            d.domain,
                    assignedAt:        d.assignedAt,
                    // Parsed JSONs for Editor
                    branding:      safeJSON(d.customBrandingJson, {}),
                    theme:         safeJSON(d.styleJson, {}),
                    topBar:        safeJSON(d.topBarJson, {}),
                    header:        safeJSON(d.headerJson, []),
                    hero:          safeJSON(d.heroJson, {}),
                    footer:        safeJSON(d.footerJson, {}),
                    aboutUs:       safeJSON(d.aboutUsJson, {}),
                    contactUs:     safeJSON(d.contactUsJson, {}),
                    seo:           safeJSON(d.seoJson, {}),
                    destinations:  safeJSON(d.destinationsJson, { destinations: [] }).destinations || [],
                    // Preserve raw strings for fields we aren't editing yet
                    layoutJson:    d.layoutJson,
                    activeModulesJson: d.activeModulesJson
                });
            } catch (e) { 
                console.error("Failed to load website config", e); 
            } finally { 
                setLoading(false); 
            }
        };
        fetchCurrentConfig();
    }, []);

    if (loading) return (
        <div className="vh-100 d-flex flex-column justify-content-center align-items-center bg-light">
            <Spinner animation="border" variant="primary" />
            <h5 className="mt-3 fw-bold text-primary">Loading Website Manager...</h5>
        </div>
    );

    return (
        <div className="website-manager-wrapper" style={{ minHeight: '100vh', background: '#f4f7fa' }}>
            <ToastContainer />
            
            {/* Top Navigation Step Bar */}
            <div style={{ background: '#1a1a1a', padding: '15px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff', position: 'sticky', top: 0, zIndex: 1000 }}>
                <div className="d-flex gap-4">
                    <div 
                        onClick={() => setStep(1)}
                        style={{ cursor: 'pointer', opacity: step === 1 ? 1 : 0.5, borderBottom: step === 1 ? '3px solid #007bff' : 'none', paddingBottom: '5px', fontWeight: 600, transition: '0.3s' }}
                    >
                        <span className="badge bg-primary me-2">1</span> Branding & Identity
                    </div>
                    <div 
                        onClick={() => setStep(2)}
                        style={{ cursor: 'pointer', opacity: step === 2 ? 1 : 0.5, borderBottom: step === 2 ? '3px solid #007bff' : 'none', paddingBottom: '5px', fontWeight: 600, transition: '0.3s' }}
                    >
                        <span className="badge bg-primary me-2">2</span> Styles & Visuals
                    </div>
                </div>
                <div className="d-none d-md-block">
                    <span className="badge bg-dark border border-secondary px-3 py-2">Editing Domain: {data?.domain}</span>
                </div>
            </div>

            {/* Content Rendering */}
            <div className="manager-content">
                {step === 1 ? (
                    <BrandingEditor initialData={data} onNext={(updatedData) => { setData(updatedData); setStep(2); }} />
                ) : (
                    <WebsiteVisualizer preloadedData={data} onBack={() => setStep(1)} />
                )}
            </div>
        </div>
    );
};

export default WebsiteManager;